// tslint:disable:max-line-length

import { isEmpty as _isEmpty } from 'lodash';
import { CMSResponseResult, INormalizedResponse, INormalizedTimeResponse, ITimeResponse } from 'Responses';
import { EpgChannel, ILoadProgramsForChannelEvent, ITypeChannelsCollection, } from './EpgChannel';
import { EpgChannelGenre } from './EpgChannelGenre';
import { EpgParser } from './EpgParser';
import { EpgProgram } from './EpgProgram';
import { EpgProgramRequestQueue, IProgramQuery, RequestedProgramType, } from './EpgProgramRequestQueue';
import { Consts, IEvent } from './index';
import { Logger } from './Logger';
import { listenForPropertiesAndDoSomething } from './miscPropertyObserver';
import { Observer } from './Observer';
import { IChannelGroup } from './ChannelGroup';
import { PropertyHolder } from './PropertyHolder';
import { Utils } from './Utils';
import List from "./lib/List";

interface ITypeSubscriptionInfoMap {
  [key: number]: string;
}

export interface ITypeExpandedGenreMap {
  [key: number]: boolean;
}

const ONE_MIN_MS = 60000;
const FORCE_UPDATE_INTERVAL = ONE_MIN_MS / 2; // 30 sec
const NORMAL_UPDATE_INTERVAL = Math.max(ONE_MIN_MS, Math.random() * ONE_MIN_MS * 5); // 1-5 min

export class Model {
  public _currentChannelId: number;
  public _activeGroupId: number | null;

  private _orderedChannels = new List<IChannelGroup>();
  private _channels: ITypeChannelsCollection = {};
  // tslint:disable-next-line:ban-types
  private _channelLoadCallback: Function;
  // tslint:disable-next-line:ban-types
  private _currentProgramLoadCallback: Function;
  // tslint:disable-next-line:ban-types
  private _timeLoadCallback: Function;
  private _programLoadingCallback: (query: string, onSuccess: Function, onError: Function) => any;
  private _timeOffset: number = 0;
  private _lang: string = '';
  private _timeUpdateId: number = -1;
  // tslint:disable-next-line:ban-types
  private _onChannelSwitch: Function;
  private _currentProgram: EpgProgram;
  private _allChannelsUrl: string;
  private _showAllChannelsButton: boolean;
  private _subscriptionsMap: ITypeSubscriptionInfoMap;
  private _programRequestQueue: EpgProgramRequestQueue;
  private _channelsToLoadHeap: number[] | null;
  private _initialCatchupTimestamp: number | undefined;
  private onExtendedProgramLoaded: Function;
  private _channelsForceUpdate: boolean;
  private _startedExpandedGenres: ITypeExpandedGenreMap | null;

  public start(options: any) {
    this._channelsForceUpdate = true;
    this._channelLoadCallback = options.channelsInfoGroup;
    this._currentProgramLoadCallback = options.epgCurrent;
    this._timeLoadCallback = options.epgTime;
    this._programLoadingCallback = options.epgProgram;
    this._programRequestQueue = new EpgProgramRequestQueue(
      this._programLoadingCallback,
    );
    this._currentChannelId = options.videoID;
    this._onChannelSwitch = options.onChannelSwitch;
    this._showAllChannelsButton = options.showAllChannelsButton || false;
    this._allChannelsUrl = options.allChannelsUrl;
    this._startedExpandedGenres = options.player ? options.player.get(Consts.EVENT_GENRE_EXPANDED_MAP) : null;
    this._lang =
      options.lang || options.player ? options.player.config.lang : 'ru';
    this._initialCatchupTimestamp = options.catchupTimestamp || undefined;
    Utils.processGenresMap(options.genresMap);

    this.onExtendedProgramLoaded = (channelId: number) => (data: any) => {
      this.onProgramLoaded.call(this, data, true, channelId);
    };

    listenForPropertiesAndDoSomething(Observer.getInstance());

    Observer.on(
        Consts.EVENT_GENRE_EXPANDED,
        this.onUIGenreExpandedStateChange.bind(this),
    );
    Observer.on(
      Consts.EVENT_NEED_TO_LOAD_UPDATED_PROGRAM_LIST,
      this.onNeedToLoadNewProgramList,
    );
    Observer.on(
      Consts.EVENT_NEED_TO_LOAD_EXTENDED_PROGRAM_LIST,
      this.onNeedToLoadNewProgramList,
    );
    Observer.on(Consts.EVENT_CHANNEL_SWITCH, this.onChannelSwitch);
    Observer.on(
      Consts.EVENT_PLAYER_FAVORITE_BUTTON_CLICK,
      this.onPlayerFavoriteButtonClick,
    );
    Observer.on(
      'property:' + Consts.PROPERTY_CURRENT_PROGRAM,
      this.onCurrentProgramChanged,
    );
    Observer.on(
      Consts.EVENT_LOAD_PROGRAMS_FOR_CHANNEL,
      this.onLoadProgramsForChannel,
    );

    PropertyHolder.registerProperty(
      Consts.PROPERTY_CURRENT_PROGRAM,
      void 0,
      true,
    );
    PropertyHolder.registerProperty(
      Consts.PROPERTY_CURRENT_CHANNEL,
      void 0,
      true,
    );
    PropertyHolder.registerProperty(Consts.PROPERTY_CURRENT_TIMESTAMP, Math.floor(Date.now() / 1000), true);
    PropertyHolder.registerProperty(Consts.PROPERTY_TIME_OFFSET, 0, true);
    PropertyHolder.registerProperty(
      Consts.PROPERTY_ALL_CHANNELS_URL,
      this._allChannelsUrl,
      true,
    );
    PropertyHolder.registerProperty(
      Consts.PROPERTY_SHOW_ALL_CHANNELS_BUTTON,
      this._showAllChannelsButton,
      true,
    );
    PropertyHolder.registerProperty(
      Consts.PROPERTY_PLAYING_PROGRAM,
      null,
      true,
    );
    PropertyHolder.registerProperty(
      Consts.PROPERTY_GET_CHANNEL_BY_ID,
      (channelId: number) => {
        return this._channels[channelId];
      },
      true
    );

    if (this._channelLoadCallback) {
      this._channelLoadCallback(undefined, this.onChannelsLoaded, this.onError);
    } else {
      this.createCurrentChannel(this._currentChannelId);
    }
    if (typeof options.getSubscriptionsInfo === 'function') {
      options.getSubscriptionsInfo(undefined, this.onSubscriptionInfoLoaded);
    }
  }

  public dispose() {
    if (this._timeUpdateId !== -1) {
      clearInterval(this._timeUpdateId);
      this._timeUpdateId = -1;
    }
  }

  public resolveActiveGroup(channel: EpgChannel = PropertyHolder.get(Consts.PROPERTY_CURRENT_CHANNEL)): number {
    if (channel.isFavorite) {
      return EpgChannelGenre.FAVORITE_GENRE_ID;
    } else if (channel.isFeatured) {
      return EpgChannelGenre.FEATURED_GENRE_ID;
    }
    return channel.originalGenre.id;
  }

  // =====================LISTENERS=======================
  private onChannelsLoaded = (data: any) => {
    const response = this.normalizeApiResponse(data);
    if (response.success) {
      [this._channels, this._orderedChannels] = EpgParser.parseChannelsData(
        response.data,
      );
      const currentId = this._currentChannelId;
      if (currentId) {
        let currentChannel: EpgChannel = PropertyHolder.get(
          Consts.PROPERTY_CURRENT_CHANNEL,
        );
        // Da WTF going here?
        if (currentChannel) {
          currentChannel.isCurrent = false;
        }
        currentChannel = this._channels[currentId];
        if (currentChannel) {
          PropertyHolder.set(Consts.PROPERTY_CURRENT_CHANNEL, currentChannel);
          currentChannel.isCurrent = true;
          this._activeGroupId = this.resolveActiveGroup(currentChannel);

          if (currentChannel.currentProgram) {
            PropertyHolder.set(
              Consts.PROPERTY_CURRENT_PROGRAM,
              currentChannel.currentProgram,
            );
          }
        }
      }
      Observer.emit(Consts.EVENT_CHANNELS_READY);

      /*
       * Create list of channels to load current programs with priority
       *  - current channel
       *  - active group
       *  - expanded groups, if present
       *  - favorite group
       *  - featured group
       */

      const channelsByGroupIndex: {[K: number]: number[]} = {};

      const loadingPriority: number[] = [this._activeGroupId as number];

      this._orderedChannels.forEach((group) => {
        channelsByGroupIndex[group.id] = group.channels as number[];
        /*
        Instead use untrusted (corrupted or not valid) data for filter,
         we should rely on them as optional hint to construct properly loading priority
           */
        if (this._startedExpandedGenres?.[group.id]) {
          loadingPriority.push(group.id);
        }
      }, {});

      const channels = loadingPriority
        .flatMap((groupId) => channelsByGroupIndex[groupId]);

      /* Ensure that current channels not just in list but the very first,
       and guarantee to loading it in the first chunk */
      channels.unshift(this._currentChannelId);

      this.loadCurrentPrograms(new Set(channels));

      this._timeLoadCallback(undefined, this.onTimeLoaded, this.onError);
    } else {
      this.onError(response.error);
    }
  }

  private onUIGenreExpandedStateChange(event: {
    name: "epg:genre:expandChange",
    data: {
      id: number;
      state: boolean;
    }
  }): void {
    if (event.data.state === false) {
      return;
    }

    this.loadGroupPrograms(event.data.id);
  }

  public loadGroupPrograms(groupId: number) {
    const group = this._orderedChannels.find((group) => {
      return group.id === groupId;
    });
    if (!group) {
      return;
    }

    const hasAllocatedPrograms = group.channels.every((channelId) => this._channels[channelId].isProgramAllocated);

    if (hasAllocatedPrograms) {
      return;
    }

    this.loadCurrentPrograms(new Set(group.channels as number[]));
  }

  private loadCurrentPrograms(channels: Set<number>) {
    const clone = new Set(channels);
    /*
    It is slightly tricky, unfortunately, we did not have clear and straightforward rules for construct URI,
    but may suggest that URI encoded channels list should be less than 4096 bytes in _summary_.

    Thous, suppose we should reserve around 200 bytes for host name, keys, sign, tokens, etc.,
     and keep in mind that each ',' will be encoded as '%2C', so actually it seems reasonable to limit
    channels list string to around 1024 * 3 character.

    May tune to achieve proper balance number of request vs. single request size/speed.
    */
    const queryLimit = 1024 * 1.5;
    const chunkList: Set<number> = new Set();
    let channelsString = '';
    for (let channelId of clone) {
      channelsString += channelId;
      chunkList.add(channelId);
      clone.delete(channelId);
      if (channelsString.length >= queryLimit) {
        break;
      }
      if (clone.size > 0){
        channelsString += ',';
      }
    }

    if (channelsString.length === 0) {
      return;
    }

    let cb = (data: string | object) => {
      this.onCurrentProgramLoaded(data, chunkList);
    }

    if (clone.size > 0) {
      /* Did it make sense to make it in parallel? */
      cb = (data: string | object) => {
        this.onCurrentProgramLoaded(data, chunkList);
        this.loadCurrentPrograms(clone);
      };
    }

    this._currentProgramLoadCallback(`?channel_id=${channelsString}&locale=${this._lang}`, cb, this.onError);
  }

  private onError = (error: any) => {
    // tslint:disable-next-line:no-console
    console.error(error);
  }

  private onTimeLoaded = (data: INormalizedTimeResponse | ITimeResponse) => {
    const response: INormalizedTimeResponse = this.normalizeApiResponse(data);
    if (response.success && response.data) {
      // @ts-ignore due to `response.data` must present in success response
      this._timeOffset =
        response.data.timestamp - Math.floor(Date.now() / 1000) || 0;
      this.determineCurrentTimestamp();

      const currentChannel: EpgChannel = PropertyHolder.get(
        Consts.PROPERTY_CURRENT_CHANNEL,
      );
      if (currentChannel) {
        this._programRequestQueue.create(
          [currentChannel.id],
          this.onProgramLoaded,
          this.onError,
          this._initialCatchupTimestamp
            ? RequestedProgramType.CUSTOM_START_FOR_CURRENT_CHANNEL
            : RequestedProgramType.SIXTEEN_HOURS,
          this._initialCatchupTimestamp || PropertyHolder.get(Consts.PROPERTY_CURRENT_TIMESTAMP),
          this._timeOffset,
          this._lang,
        );
      }

      // force finding of current program
      this.onGlobalUpdate();
    } else {
      this._timeOffset = 0;
      this.onError(response.error);
    }
    PropertyHolder.set(Consts.PROPERTY_TIME_OFFSET, this._timeOffset);
  }

  private onProgramLoaded = (
    data: any,
    isExtended: boolean,
    channelId: number,
  ) => {
    const response = this.normalizeApiResponse(data);
    if (response.success) {
      const programs = EpgParser.parseProgramsData(
        response.data,
        isExtended,
        channelId,
      );
      Utils.allocateProgramsToChannels(programs, this._channels);

      // at this point we suppose that all are ready and need to begin process programs
      if (this._timeUpdateId === -1) {
        this._timeUpdateId = window.setInterval(
          this.onGlobalUpdate.bind(this),
          1000,
        );
      }
      this.onGlobalUpdate();

      Observer.emit(Consts.EVENT_PROGRAMS_READY, this._channels);
    } else {
      this.onError(response.error);
    }
    return response;
  }

  private onNeedToLoadNewProgramList = (event: any) => {
    const data = event.data;
    if (data) {
      Logger.log(
        'NEED TO LOAD NEW PROGRAM. CHANNEL:',
        data.title,
        'EXTENDED PROGRAM:',
        data.isCurrent,
      );

      let type: RequestedProgramType = data.type;
      if (event.name === Consts.EVENT_NEED_TO_LOAD_EXTENDED_PROGRAM_LIST) {
        type = RequestedProgramType.EXTENDED;
      } else if (data.type === RequestedProgramType.ONE_HOUR) {
        if (!this._channelsToLoadHeap) {
          this._channelsToLoadHeap = [data.channel.id];
          window.setTimeout(this.onLoadChannelsHeap, this._channelsForceUpdate ? FORCE_UPDATE_INTERVAL : NORMAL_UPDATE_INTERVAL);
          this._channelsForceUpdate = false; // Force load first heap
        } else if (!this._channelsToLoadHeap.includes(data.channel.id)) {
          this._channelsToLoadHeap.push(data.channel.id);
        }

        // do not execute below code
        return;
        // ───────────────────────────────────
        // ───────────▄▄█▀██▀█▄▄▄▄────────────
        // ─────────▄█▀▀████▄▄█▀▀▀▀█▄▄────────
        // ───────▄█░░░░░░░░░░░░░░░░░░█▄──────
        // ─────▄██░░░░░░░░░░░░░░░░░░░░▀█▄────
        // ────▄██░░░░░░░░░░░░░░░░░░░░░░░██───
        // ────█▀░░░░░░░░▄░░░░░░░░░░▄░░░░░▀█▄─
        // ───█▀░░░░▄▀▀▀▀░█░░░░░▄▀▀▀░▀▄░░░░░█▄
        // ──██░░░░█░▄▀▀▄░█░░░░█░▄▀▀▄░░█░░░░██
        // ─██░░░░░█░░▀▀░▄▀░░░░▀▄░▀▀░░▄▀░░░░░█
        // ██▀░░░░░▀▀▀▀▀▀░░░░░░░░▀▀▀▀▀░░░░░░░█
        // ██░░░░░░░░░░░░░▐░░░░░░░░░░░░░░░░░░█
        // █▀░░░░░░░░░░░░░▌░░░░▌░░░░░░░░░░░░░█
        // █░░░░░░░░░░░░░▐░▄░▄░▐░░░░░░░░░░░░░█
        // █░░░░░░░░▐░░░░░░░░░░░░░░░░░░░░░░░░█
        // █░░░░░░░░▌░░░░░░░░░░░░░░░░░░░░░░░██
        // █░░░░░░░▐░░▄▀▀▀▀▀▀▀▀▄░░░░░░░░░░░░██
        // ─█░░░░░░░▄▀▄████████▄█▄░▌░░░░░░░██─
        // ──█░░░░░░█▄█▄▄█▄▄█▄▄██▀░▐░░░░░▄██──
        // ──▀▄░░░░░░▀█▄▄▄▄▄▄▄▄▀░░░░░░░▄█▀────
        // ────█▄░░▀▄░░░░░░░░░░░░░░░░▄█▀──────
        // ─────█▄▄░░▀░░░░░░░░░░░░░▄█▀────────
        // ───────▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀▀──────────
        // ───────────────────────────────────
      }

      this._programRequestQueue.create(
        [data.channel.id],
        type === RequestedProgramType.EXTENDED
          ? this.onExtendedProgramLoaded(data.channel.id)
          : this.onProgramLoaded,
        this.onError,
        type,
        PropertyHolder.get(Consts.PROPERTY_CURRENT_TIMESTAMP),
        this._timeOffset,
        this._lang,
      );
    }
  }

  private onLoadChannelsHeap = () => {
    if (this._channelsToLoadHeap) {
      this._programRequestQueue.create(
        this._channelsToLoadHeap,
        this.onProgramLoaded,
        this.onError,
        RequestedProgramType.ONE_HOUR,
        PropertyHolder.get(Consts.PROPERTY_CURRENT_TIMESTAMP),
        this._timeOffset,
        this._lang,
      );
    }
    this._channelsToLoadHeap = null;
  }

  private onGlobalUpdate = () => {
    const currentTimestamp: number = this.determineCurrentTimestamp();

    if (this._currentProgram) {
      PropertyHolder.set(
        Consts.PROPERTY_CURRENT_PROGRAM_TIME,
        currentTimestamp - this._currentProgram.startTimestamp,
      );
    } else {
      PropertyHolder.set(Consts.PROPERTY_CURRENT_PROGRAM_TIME, -1);
    }

    Observer.emit(Consts.EVENT_TIME_UPDATE);
    this._programRequestQueue.onTimeUpdate();
  }

  private onChannelSwitch = (event: IEvent) => {
    const channel: EpgChannel = this._channels[event.data.id];
    /*
     * Due to we could not determine to which group channel belongs, we should query for all groups
     * suppose it's not a big deal, because loadGroupPrograms will check if programs already loaded.
     * An alternative: modify coreEPG#switchChannel in EPG UI project to pass group id as well.
     */
    this._orderedChannels
      .filter((group) => group.channels.includes(channel.id))
      .forEach((group) => {
        this.loadGroupPrograms(group.id);
      });

    const currentChannel: EpgChannel = PropertyHolder.get(
      Consts.PROPERTY_CURRENT_CHANNEL,
    );
    if (channel && (!currentChannel || channel.id !== currentChannel.id)) {
      this._currentChannelId = channel.id;
      if (currentChannel) {
        currentChannel.isCurrent = false;
      }
      channel.isCurrent = true;
      PropertyHolder.set(Consts.PROPERTY_CURRENT_CHANNEL, channel);
      if (channel.currentProgram) {
        PropertyHolder.set(
          Consts.PROPERTY_CURRENT_PROGRAM,
          channel.currentProgram,
        );
      }
      if (!channel.hasExtendedProgram) {
        Observer.emit(Consts.EVENT_NEED_TO_LOAD_UPDATED_PROGRAM_LIST, {
          channel,
          type: RequestedProgramType.SIXTEEN_HOURS,
        });
      }
      if (typeof this._onChannelSwitch === 'function') {
        this._onChannelSwitch(channel);
      }
    }
  }

  private onPlayerFavoriteButtonClick = (event: any) => {
    const channel: EpgChannel = PropertyHolder.get(
      Consts.PROPERTY_CURRENT_CHANNEL,
    );
    if (!channel) {
      return;
    }
    channel.toggleFavoriteCategory(!event.data.state);
    Observer.emit(Consts.EVENT_FAVORITE_TOGGLED, {
      state: !event.data.state,
      channel,
    });
  }

  private onCurrentProgramChanged = (event: any) => {
    this._currentProgram = event.data;
  }

  private onCurrentProgramLoaded = (data: string | object, requestedChannels: Set<number>) => {
    const response = this.normalizeApiResponse(data);
    if (response.success && Array.isArray(response.data.current)) {
      /* response may not contain *all* requested channels,
        it's mean channel does not have program,
         but it's explicit and legal "no program" state */
      requestedChannels.forEach(channelId => {
        this._channels[channelId].isProgramAllocated = true;
      });
      Utils.allocateProgramsToChannels(
        EpgParser.parseProgramsData(response.data.current),
        this._channels,
      );
      this.onGlobalUpdate();
      Observer.emit(Consts.EVENT_PROGRAMS_READY, this._channels);
    }
  }

  private onLoadProgramsForChannel = (event: any) => {
    const data: ILoadProgramsForChannelEvent = event.data as ILoadProgramsForChannelEvent;
    if (data) {
      const request: IProgramQuery = {
        channelId: data.id.toString(),
        from:
          data.direction === 'past'
            ? data.timestamp - data.period
            : data.timestamp,
        to:
          data.direction === 'past'
            ? data.timestamp
            : data.timestamp + data.period,
        lang: this._lang,
        onComplete: (...args: any[]) => {
          const onProgramLoaded = this.onProgramLoaded;
          const response = this.onProgramLoaded(...(args as Parameters<typeof onProgramLoaded>));
          if (data.resolve) {
            data.resolve(response);
          }
        },
        onError: (...args: any[]) => {
          const onError = this.onError;
          this.onError(...(args as Parameters<typeof onError>));
          if (data.reject) {
            data.reject(...args);
          }
        },
      };

      /*
      Для работы web mobile EPG UI (в текущей реализации)
      необходимо името программу на опеделенный день и точно знать что это __вся__ праграмма именно на этот день.
      В свзяи с этим некоторый запросы на загрузку программ не должны попадать в очередь запросов.
      Негативный эффект - увеличение количества запросов при переходе по каналам,
       jAPI может пережить это, по крайней мере пока.
       TODO: пересмотреть загрузку программ (и прочего).
       */
      if (data.exclusiveRequest) {
        const queryString: string = `?channel_id=${request.channelId}&locale=${request.lang}&from=${request.from}&to=${request.to}`;
        this._programLoadingCallback(queryString, request.onComplete, request.onError);
      } else {
        this._programRequestQueue.add(request);
      }
    }
  };

  private onSubscriptionInfoLoaded = (_response_: string) => {
    let response = this.normalizeApiResponse(_response_);
    if (response.success && response.data) {
      this._subscriptionsMap = {};
      response.data.forEach((elem: any) => {
        this._subscriptionsMap[elem.subscription_id] = elem.title;
      });
      Observer.emit(
        Consts.EVENT_SUBSCRIPTION_INFO_LOADED,
        this._subscriptionsMap,
      );
    }
  }

  // ==========================MISCELLANEOUS=============================

  private createCurrentChannel = (channelId: number) => {
    const onVideoInfo = (event: IEvent) => {
      const { data } = event;
      if (!_isEmpty(data.videoInfo)) {
        Observer.off('property:videoInfo', onVideoInfo);
        const channel = new EpgChannel({
          id: channelId,
          title: videoInfo.title,
          vod_channel: videoInfo.content_type.includes('m_ch'),
          is_dvr: videoInfo.content_type.includes('tv_dvr'),
          is_favorite: false,
          is_available: Boolean(videoInfo.src),
        });
        this._channels = { [channelId]: channel };
        // this._orderedChannels = [];
        PropertyHolder.set(Consts.PROPERTY_CURRENT_CHANNEL, channel);
        channel.isCurrent = true;
        Observer.emit(Consts.EVENT_CHANNELS_READY);
        // this._currentProgramLoadCallback(this.createCurrentProgramsQuery(this._channels), this.onCurrentProgramLoaded, this.onError);
        this._timeLoadCallback(undefined, this.onTimeLoaded, this.onError);
      }
    };
    const videoInfo: any = PropertyHolder.get('videoInfo');
    if (!_isEmpty(videoInfo)) {
      onVideoInfo({ name: '', data: videoInfo });
    } else {
      Observer.on('property:videoInfo', onVideoInfo);
    }
  }

  private normalizeApiResponse(data: string | object): INormalizedResponse {
    if (typeof data === 'string') {
      const result = JSON.parse(data);
      const success = result.result === CMSResponseResult.ok, failure = !success;
      return {
        ...result,
        success,
        failure,
      };
    } else if (!('result' in data) && !_isEmpty(data)) {
      return {
        result: CMSResponseResult.ok,
        success: true,
        failure: false,
        data,
      };
    }

    return {
      result: CMSResponseResult.error,
      success: false,
      failure: false,
    }
  }

  private determineCurrentTimestamp(): number {
    const currentTimestamp: number =
      Math.floor(Date.now() / 1000) + this._timeOffset;
    PropertyHolder.set(Consts.PROPERTY_CURRENT_TIMESTAMP, currentTimestamp);
    return currentTimestamp;
  }

  private createCurrentProgramsQuery(
    channels: ITypeChannelsCollection,
  ): string {
    const keys = Object.keys(this.channels);
    return `?channel_id=${keys.join(',')}&locale=${this._lang}`;
  }

  public get orderedChannels(): List<IChannelGroup> {
    return this._orderedChannels;
  }

  public get channels(): ITypeChannelsCollection {
    return this._channels;
  }

  public get subscriptionsMap(): ITypeSubscriptionInfoMap {
    return this._subscriptionsMap;
  }

  public get allChannelsUrl(): string {
    return this._allChannelsUrl;
  }

  public get showAllChannelsButton(): boolean {
    return this._showAllChannelsButton;
  }
}
